import { forwardRef, useEffect, useMemo, useState } from "react";
import {
  Button,
  Form,
  Modal,
  Spinner,
  Tab,
  Table,
  Tabs,
} from "react-bootstrap";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { format } from "date-fns";
import axios from "axios";
import swal from "sweetalert";
import BASE_URL from "../../../../assets/global/url";
import { formatToTwoDecimal } from "../../../../utils/numberFormatter";
import CustomDatePicker from "../../../../components/CustomDatePicker";
import TypedDateInput from "../../../../components/inputs/TypedDateInput";
import useCompanyProfile from "../../../../hooks/customHook/useCompanyProfile";
import AccountSummaryPDF, { RangeSummaryPDF } from "./AccountSummaryPDF";
import {
  balanceBefore,
  buildCompanyInfo,
  buildDailySummaries,
  buildRangeSummary,
  displayAmount,
  downloadAccountSummaryExcel,
  downloadRangeSummaryExcel,
  RANGE_COLUMNS,
  SUMMARY_COLUMNS,
} from "./accountSummaryData";

// Export preview for the View Account transaction history.
// Step 1 asks for a date range and a layout:
//   "daily"   - one tab per day that has transactions
//   "summary" - the whole range in one table, no tabs
// Step 2 previews it: type "pdf" shows the PDF, type "excel" shows the sheet
// as a table; the Download button sits below the preview in the footer.

// Shared typed date input with a fixed class: react-datepicker passes its own
// className (e.g. "react-datepicker-ignore-onclickoutside" while the calendar
// is open), which would replace form-control and leave a bare black input
const DateInput = forwardRef((props, ref) => (
  <TypedDateInput {...props} className="form-control p-2 w-100" ref={ref} />
));
DateInput.displayName = "DateInput";

// Export is not paged - fetch the whole range in one request
const EXPORT_LIMIT = 100000;

// Letterhead + right-aligned date line + black title band, same as the files
const SheetHeader = ({ company, dateText, titleText }) => (
  <>
    {/* Letterhead: logo top-left, name + details centered */}
    <div
      className="position-relative border-bottom border-dark pb-2"
      style={{ minHeight: 90 }}
    >
      {company.logo && (
        <img
          src={company.logo}
          alt="Company logo"
          className="position-absolute top-0 start-0"
          style={{ width: 130, height: 90, objectFit: "contain" }}
        />
      )}
      <div className="text-center fw-bold fs-1">{company.name}</div>
      {company.lines.map((line) => (
        <div key={line} className="text-center small fw-bold">
          {line}
        </div>
      ))}
    </div>
    <div className="text-end small mt-2 mb-1">{dateText}</div>
    <div
      className="text-center fw-bold py-1"
      style={{ backgroundColor: "#000", color: "#fff" }}
    >
      {titleText}
    </div>
  </>
);

// Whole range in one table, same layout as the Summary Excel sheet. The
// NAME | TOTAL and ACCOUNT TITLE side tables are only in the downloaded file
const RangeSheet = ({ summary, title, company, period }) => {
  return (
    <>
      <SheetHeader company={company} dateText={period} titleText={title} />
      <Table bordered size="sm" className="mb-0">
        <thead>
          <tr>
            {RANGE_COLUMNS.map((column, index) => (
              <th
                key={column}
                className={index >= 5 ? "text-end" : "text-center"}
              >
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          <tr className="fw-bold">
            <td colSpan={4}></td>
            <td className="text-end">CASH BALANCE</td>
            <td colSpan={2}></td>
            <td className="text-end">
              {formatToTwoDecimal(summary.openingBalance)}
            </td>
          </tr>
          {summary.rows.map((row) => (
            <tr key={row.ref}>
              <td className="text-nowrap">{row.date}</td>
              <td className="text-center">{row.ref}</td>
              <td>{row.payee}</td>
              <td>{row.accountTitle}</td>
              <td>{row.particulars}</td>
              <td className="text-end text-success">
                {row.cashIn ? formatToTwoDecimal(row.cashIn) : ""}
              </td>
              <td className="text-end text-danger">
                {row.cashOut ? formatToTwoDecimal(row.cashOut) : ""}
              </td>
              <td className="text-end">{formatToTwoDecimal(row.balance)}</td>
            </tr>
          ))}
          <tr className="fw-bold">
            <td colSpan={5} className="text-end">
              TOTAL
            </td>
            <td className="text-end">
              {formatToTwoDecimal(summary.totalCashIn)}
            </td>
            <td className="text-end">
              {formatToTwoDecimal(summary.totalCashOut)}
            </td>
            <td
              className="text-end"
              style={{ backgroundColor: "#ffc000", color: "#000" }}
            >
              {formatToTwoDecimal(summary.balance)}
            </td>
          </tr>
        </tbody>
      </Table>
    </>
  );
};

// One day's sheet, same layout as a sheet in the downloaded Excel file
const DaySheet = ({ day, title, company }) => (
  <>
    <SheetHeader
      company={company}
      dateText={day.date}
      titleText={`${title} ${day.label}`}
    />
    <Table bordered size="sm" className="mb-0">
      <thead>
        <tr>
          {SUMMARY_COLUMNS.map((column, index) => (
            <th
              key={column}
              className={index === 4 ? "text-end" : "text-center"}
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colSpan={4} className="text-end fw-bold fst-italic">
            CASH BALANCE
          </td>
          <td className="text-end fw-bold">
            {formatToTwoDecimal(day.openingBalance)}
          </td>
        </tr>
        {day.rows.map((row) => (
          <tr key={row.ref}>
            <td className="text-center">{row.ref}</td>
            <td>{row.payee}</td>
            <td>{row.accountTitle}</td>
            <td>{row.particulars}</td>
            <td
              className={`text-end ${
                row.type === "Deposit" ? "text-success" : "text-danger"
              }`}
            >
              {displayAmount(row)}
            </td>
          </tr>
        ))}
        <tr>
          <td colSpan={4} className="text-end fw-bold">
            TOTAL WITHDRAW
          </td>
          <td className="text-end fw-bold">
            {formatToTwoDecimal(day.totalWithdraw)}
          </td>
        </tr>
        <tr>
          <td colSpan={4} className="text-end fw-bold">
            TOTAL DEPOSIT
          </td>
          <td className="text-end fw-bold">
            {formatToTwoDecimal(day.totalDeposit)}
          </td>
        </tr>
        <tr>
          <td colSpan={4} className="text-end fw-bold">
            BALANCE
          </td>
          <td
            className="text-end fw-bold"
            style={{ backgroundColor: "#ffc000", color: "#000" }}
          >
            {formatToTwoDecimal(day.balance)}
          </td>
        </tr>
      </tbody>
    </Table>
  </>
);

const AccountSummaryExportModal = ({
  show,
  type,
  onHide,
  accountId,
  accountName,
  // "Account Report" Import/Export permission; without it it's preview only
  canDownload = false,
}) => {
  const [step, setStep] = useState("range"); // "range" | "preview"
  const [layout, setLayout] = useState("daily"); // "daily" | "summary"
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeDay, setActiveDay] = useState("#1");
  // Report header uses the company name from Company Profile
  const { company } = useCompanyProfile();
  const companyInfo = useMemo(() => buildCompanyInfo(company), [company]);

  // Default range: the cutoff covering today, else the latest cutoff
  const loadCutoffRange = async () => {
    let cutoff = null;
    try {
      const res = await axios.get(BASE_URL + "/cutoff/getCurrentCutOff");
      cutoff = res.data;
    } catch (error) {
      // 404 = no cutoff covers today, fall back to the latest one
      try {
        const res = await axios.get(BASE_URL + "/cutoff/latest-cutoff");
        cutoff = res.data;
      } catch (err) {
        console.error(err);
      }
    }

    if (cutoff?.from && cutoff?.to) {
      setFromDate(new Date(cutoff.from));
      setToDate(new Date(cutoff.to));
    }
  };

  // Every open starts at the date range, prefilled with the cutoff period
  useEffect(() => {
    if (show) {
      setStep("range");
      setLayout("daily");
      setFromDate("");
      setToDate("");
      setTransactions([]);
      loadCutoffRange();
    }
  }, [show]);

  const days = useMemo(
    () => buildDailySummaries(transactions, openingBalance),
    [transactions, openingBalance],
  );

  const rangeSummary = useMemo(
    () => buildRangeSummary(days, openingBalance, fromDate),
    [days, openingBalance, fromDate],
  );

  const period =
    fromDate && toDate
      ? `${format(new Date(fromDate), "MMM dd, yyyy")} - ${format(
          new Date(toDate),
          "MMM dd, yyyy",
        )}`
      : "";

  const loadPreview = async () => {
    if (!fromDate || !toDate) {
      swal({
        icon: "error",
        title: "Required Fields",
        text: "Please select both From and To dates.",
      });
      return;
    }
    if (new Date(fromDate) > new Date(toDate)) {
      swal({
        icon: "error",
        title: "Invalid Date Range",
        text: "From date must be on or before the To date.",
      });
      return;
    }

    setLoading(true);
    try {
      // Range rows for the tabs + every row of the account for the opening
      // balance before the From date
      const [res, balanceRes] = await Promise.all([
        axios.get(BASE_URL + "/accountListSub/getTransaction", {
          params: {
            id: accountId,
            startDate: new Date(fromDate).toLocaleDateString(),
            endDate: new Date(toDate).toLocaleDateString(),
            sortType: "desc",
            sortDBTableColumn: "date",
            page: 1,
            limit: EXPORT_LIMIT,
          },
        }),
        axios.get(BASE_URL + "/accountListSub/getBalance", {
          params: { id: accountId },
        }),
      ]);
      setTransactions(res.data.data || []);
      setOpeningBalance(balanceBefore(balanceRes.data.data || [], fromDate));
      setActiveDay("#1");
      setStep("preview");
    } catch (error) {
      console.error(error);
      swal({
        icon: "error",
        title: "Something went wrong",
        text: "Unable to load transactions for the selected dates.",
      });
    } finally {
      setLoading(false);
    }
  };

  const title = `${accountName || "ACCOUNT"} SUMMARY`.toUpperCase();
  const fileName = `${accountName || "Account"} Summary`;
  const header = { title, company: companyInfo };
  const isSummary = layout === "summary";

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      if (isSummary) {
        await downloadRangeSummaryExcel(rangeSummary, {
          ...header,
          period,
          fileName,
        });
      } else {
        await downloadAccountSummaryExcel(days, { ...header, fileName });
      }
    } catch (error) {
      console.error(error);
      swal({
        icon: "error",
        title: "Something went wrong",
        text: "Unable to create the Excel file.",
      });
    } finally {
      setDownloading(false);
    }
  };

  if (step === "range") {
    return (
      <Modal show={show} onHide={onHide} size="md" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Export {type === "pdf" ? "PDF" : "Excel"} - Select Date Range
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="row">
            <div className="col-sm mb-2">
              <span>From</span>
              <CustomDatePicker
                selected={fromDate ? new Date(fromDate) : ""}
                handleDateChange={(date) => setFromDate(date)}
                setter={setFromDate}
                CustomInput={DateInput}
                maxDate={toDate ? new Date(toDate) : undefined}
              />
            </div>
            <div className="col-sm mb-2">
              <span>To</span>
              <CustomDatePicker
                selected={toDate ? new Date(toDate) : ""}
                handleDateChange={(date) => setToDate(date)}
                setter={setToDate}
                CustomInput={DateInput}
                minDate={fromDate ? new Date(fromDate) : undefined}
              />
            </div>
          </div>

          <div className="mt-2">
            <span>Layout</span>
            <Form.Check
              type="radio"
              id="export-layout-daily"
              name="export-layout"
              label="Per day - one tab for each day with transactions"
              checked={layout === "daily"}
              onChange={() => setLayout("daily")}
            />
            <Form.Check
              type="radio"
              id="export-layout-summary"
              name="export-layout"
              label="Summary - whole date range in one table, no tabs"
              checked={layout === "summary"}
              onChange={() => setLayout("summary")}
            />
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="success"
            onClick={loadPreview}
            disabled={loading || !fromDate || !toDate}
          >
            {loading ? (
              <Spinner animation="border" size="sm" className="me-2" />
            ) : (
              <i className="fa-solid fa-eye me-2"></i>
            )}
            {loading ? "Loading..." : "Preview"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    // 90% of the screen wide and tall; the body fills whatever the header
    // and footer leave
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="custom-modal-vw90"
      contentClassName="h-100"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {type === "pdf" ? "PDF Preview" : "Excel Preview"}
          {period && <span className="fs-6 ms-2 text-muted">({period})</span>}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        {days.length === 0 ? (
          <div className="text-center text-muted py-5">
            No transactions to export for the selected dates.
          </div>
        ) : isSummary ? (
          // Whole range in one table, no tabs
          type === "pdf" ? (
            <PDFViewer
              style={{ width: "100%", height: "100%", border: 0 }}
              showToolbar={canDownload}
            >
              {/* Signatures and the NAME / ACCOUNT TITLE count tables only
                  go on the downloaded file */}
              <RangeSummaryPDF
                summary={rangeSummary}
                period={period}
                showSignatures={false}
                showCountTables={false}
                {...header}
              />
            </PDFViewer>
          ) : (
            <RangeSheet summary={rangeSummary} period={period} {...header} />
          )
        ) : (
          // One tab per day that has transactions; days without any are skipped
          <Tabs
            activeKey={activeDay}
            onSelect={(key) => setActiveDay(key)}
            className="mb-3"
            mountOnEnter
            unmountOnExit
          >
            {days.map((day) => (
              <Tab key={day.label} eventKey={day.label} title={day.label}>
                {type === "pdf" ? (
                  <PDFViewer
                    style={{
                      width: "100%",
                      // Room left after header, tabs and footer
                      height: "calc(90vh - 230px)",
                      border: 0,
                    }}
                    showToolbar={canDownload}
                  >
                    {/* Signatures only go on the downloaded file */}
                    <AccountSummaryPDF
                      days={[day]}
                      showSignatures={false}
                      {...header}
                    />
                  </PDFViewer>
                ) : (
                  <DaySheet day={day} {...header} />
                )}
              </Tab>
            ))}
          </Tabs>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide}>
          Close
        </Button>
        <Button variant="outline-primary" onClick={() => setStep("range")}>
          <i className="fa-solid fa-calendar-days me-2"></i>
          Change Dates
        </Button>
        {!canDownload ? null : days.length === 0 ? (
          <Button variant="success" disabled>
            <i className="fa-solid fa-download me-2"></i>
            Download {type === "pdf" ? "PDF" : "Excel"}
          </Button>
        ) : type === "pdf" ? (
          // Per day: each day starts on its own page; Summary: one table
          <PDFDownloadLink
            document={
              isSummary ? (
                <RangeSummaryPDF
                  summary={rangeSummary}
                  period={period}
                  {...header}
                />
              ) : (
                <AccountSummaryPDF days={days} {...header} />
              )
            }
            fileName={`${fileName}.pdf`}
          >
            {({ loading }) => (
              <Button variant="success" disabled={loading}>
                <i className="fa-solid fa-download me-2"></i>
                {loading ? "Preparing..." : "Download PDF"}
              </Button>
            )}
          </PDFDownloadLink>
        ) : (
          // Per day: one sheet per day; Summary: one "Summary" sheet
          <Button
            variant="success"
            onClick={downloadExcel}
            disabled={downloading}
          >
            <i className="fa-solid fa-download me-2"></i>
            {downloading ? "Preparing..." : "Download Excel"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default AccountSummaryExportModal;
